import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { api } from '@/shared/api';
import {
  FileText,
  ListChecks,
  Gavel,
  GitCompareArrows,
  CalendarClock,
  Activity,
  Users,
  Building2,
  LayoutDashboard,
} from 'lucide-react';

interface KPI {
  total_bap: number;
  ready_bap: number;
  pending_bap: number;
  pages_analyzed: number;
  total_users: number;
  audit_events_today: number;
  total_findings: number;
  total_violations: number;
  total_inconsistencies: number;
  total_timeline_events: number;
}

interface TrendPoint { day: string; count: number; }
interface TopViolation { regulation_id: string; regulation_name: string; regulation_category: string; hits: number; avg_confidence: number; }
interface CategoryRow { category: string; count: number; avg_severity: number; }
interface SatkerRow { satker: string; bap_count: number; pages: number; }
interface RecentBAP {
  id: string;
  bap_number: string | null;
  case_id: string | null;
  satker: string;
  classification: string;
  status: string;
  page_count: number | null;
  uploaded_at: string | null;
}
interface SeverityBucket { bucket: string; count: number; }

const CATEGORY_LABEL: Record<string, { name: string; color: string }> = {
  PELANGGARAN:      { name: 'Pelanggaran',  color: 'bg-sev-critical' },
  ANOMALI:          { name: 'Anomali',      color: 'bg-sev-high' },
  FAKTA_KUNCI:      { name: 'Fakta Kunci',  color: 'bg-sev-medium' },
  PERTANYAAN_LANJUT:{ name: 'Pertanyaan',   color: 'bg-bluestamp' },
  KONTEKS:          { name: 'Konteks',      color: 'bg-sev-low' },
};

const SEV_BUCKET_COLOR: Record<string, string> = {
  '0-2': 'bg-sev-low',
  '2-4': 'bg-sev-low/70',
  '4-6': 'bg-sev-medium',
  '6-8': 'bg-sev-high',
  '8-10': 'bg-sev-critical',
};

const KLAS_COLOR: Record<string, string> = {
  BIASA: 'text-ink-2',
  TERBATAS: 'text-bluestamp',
  RAHASIA: 'text-maroon',
  SANGAT_RAHASIA: 'text-crimson',
};

export default function DashboardPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Delay slightly to allow render
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [location.hash]);

  const { data: kpi, isLoading } = useQuery<KPI>({
    queryKey: ['dashboard-kpi'],
    queryFn: () => api('/api/v1/dashboard/kpi'),
  });
  const { data: trend } = useQuery<TrendPoint[]>({
    queryKey: ['dashboard-trend'],
    queryFn: () => api('/api/v1/dashboard/upload-trend?days=30'),
  });
  const { data: topViol } = useQuery<TopViolation[]>({
    queryKey: ['dashboard-top-violations'],
    queryFn: () => api('/api/v1/dashboard/top-violations?limit=8'),
  });
  const { data: findCat } = useQuery<CategoryRow[]>({
    queryKey: ['dashboard-categories'],
    queryFn: () => api('/api/v1/dashboard/findings-categories'),
  });
  const { data: satkers } = useQuery<SatkerRow[]>({
    queryKey: ['dashboard-satker'],
    queryFn: () => api('/api/v1/dashboard/satker-distribution?limit=8'),
  });
  const { data: recent } = useQuery<RecentBAP[]>({
    queryKey: ['dashboard-recent'],
    queryFn: () => api('/api/v1/dashboard/recent-bap?limit=8'),
  });
  const { data: sevBuckets } = useQuery<SeverityBucket[]>({
    queryKey: ['dashboard-severity'],
    queryFn: () => api('/api/v1/dashboard/severity-distribution'),
  });

  return (
    <div>
      <div className="mb-4 pb-2 border-b-2 border-ink flex items-baseline gap-3 flex-wrap">
        <LayoutDashboard className="w-5 h-5 text-brass self-center" />
        <h1 className="font-display font-bold text-[22px] leading-tight tracking-tight">Dashboard</h1>
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">— Beranda · Eksekutif</span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-ink-3">Ringkasan operasional · realtime</span>
      </div>

      {isLoading && <p className="font-mono text-sm text-ink-2">Memuat KPI...</p>}

      {/* KPI STRIP */}
      {kpi && (
        <div id="kpi" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-t border-b border-ink-line mb-10 scroll-mt-[220px]">
          <KPICell Icon={FileText} label="Total BAP" value={kpi.total_bap} foot={`READY ${kpi.ready_bap} · PENDING ${kpi.pending_bap}`} />
          <KPICell Icon={ListChecks} label="Temuan" value={kpi.total_findings} foot="seluruh kategori" />
          <KPICell Icon={Gavel} label="Pasal Indikatif" value={kpi.total_violations} foot="cross-doc" />
          <KPICell Icon={GitCompareArrows} label="Kontradiksi" value={kpi.total_inconsistencies} foot="cross-doc" />
          <KPICell Icon={CalendarClock} label="Event Kronologi" value={kpi.total_timeline_events} foot="bertanggal" last />
        </div>
      )}

      {/* TWO COLUMNS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
        {/* UPLOAD TREND */}
        <section id="trend" className="scroll-mt-[220px]">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-ink-line">
            <Activity className="w-4 h-4 text-brass" />
            <h2 className="font-display font-bold text-[15px] tracking-tight">Tren Unggah (30 hari)</h2>
            <span className="font-mono text-[10px] tracking-widest text-ink-3 ml-auto">VOLUMEN</span>
          </div>
          {trend && <SparklineChart data={trend} />}
        </section>

        {/* SEVERITY DISTRIBUTION */}
        <section id="severity" className="scroll-mt-[220px]">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-ink-line">
            <Activity className="w-4 h-4 text-brass" />
            <h2 className="font-display font-bold text-[15px] tracking-tight">Distribusi Severity Temuan</h2>
            <span className="font-mono text-[10px] tracking-widest text-ink-3 ml-auto">SEVERITAS</span>
          </div>
          {sevBuckets && (
            <div className="space-y-2 font-mono text-[11px]">
              {sevBuckets.map((b) => {
                const max = Math.max(1, ...(sevBuckets || []).map((x) => x.count));
                const pct = (b.count / max) * 100;
                return (
                  <div key={b.bucket} className="flex items-center gap-3">
                    <span className="w-12 text-ink-3 tabular-nums">{b.bucket}</span>
                    <div className="flex-1 h-5 bg-paper-2/30 border border-ink-line/40 relative">
                      <div
                        className={`h-full ${SEV_BUCKET_COLOR[b.bucket]}`}
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                    <span className="w-10 text-right text-ink tabular-nums font-bold">{b.count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* TOP VIOLATIONS + FINDINGS CATEGORIES */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
        <section id="top-pasal" className="scroll-mt-[220px]">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-ink-line">
            <Gavel className="w-4 h-4 text-brass" />
            <h2 className="font-display font-bold text-[15px] tracking-tight">Top Pasal Indikatif</h2>
            <span className="font-mono text-[10px] tracking-widest text-ink-3 ml-auto">ARTICULI</span>
          </div>
          {topViol && topViol.length > 0 ? (
            <div className="space-y-1.5">
              {topViol.map((v) => {
                const max = Math.max(1, ...(topViol || []).map((x) => x.hits));
                const pct = (v.hits / max) * 100;
                return (
                  <div key={v.regulation_id} className="grid grid-cols-[1fr_auto] items-center gap-3 font-mono text-[11px] py-1">
                    <div>
                      <div className="text-ink font-bold">{v.regulation_name}</div>
                      <div className="text-ink-3 text-[10px] uppercase tracking-wider">{v.regulation_category}</div>
                      <div className="h-1.5 bg-paper-2/30 mt-1 border border-ink-line/40 relative">
                        <div className="h-full bg-maroon" style={{ width: `${pct}%` }}></div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-[18px] font-bold tabular-nums leading-none">{v.hits}</div>
                      <div className="text-ink-3 text-[9px] uppercase tracking-wider">{(v.avg_confidence * 100).toFixed(0)}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="font-mono text-xs text-ink-3 italic">Belum ada pasal indikatif terdaftar.</p>
          )}
        </section>

        <section id="kategori" className="scroll-mt-[220px]">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-ink-line">
            <ListChecks className="w-4 h-4 text-brass" />
            <h2 className="font-display font-bold text-[15px] tracking-tight">Kategori Temuan</h2>
            <span className="font-mono text-[10px] tracking-widest text-ink-3 ml-auto">INVENTIONES</span>
          </div>
          {findCat && findCat.length > 0 ? (
            <div className="space-y-2 font-mono text-[11px]">
              {findCat.map((c) => {
                const cs = CATEGORY_LABEL[c.category] || { name: c.category, color: 'bg-ink-2' };
                const max = Math.max(1, ...(findCat || []).map((x) => x.count));
                const pct = (c.count / max) * 100;
                return (
                  <div key={c.category} className="grid grid-cols-[100px_1fr_auto] items-center gap-3">
                    <span className="text-ink-2 font-bold uppercase tracking-wider text-[10px]">{cs.name}</span>
                    <div className="h-4 bg-paper-2/30 border border-ink-line/40 relative">
                      <div className={`h-full ${cs.color}`} style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className="tabular-nums font-bold text-ink min-w-[40px] text-right">
                      {c.count} <span className="text-ink-3 text-[9px]">⌀{c.avg_severity.toFixed(1)}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="font-mono text-xs text-ink-3 italic">Belum ada temuan terdaftar.</p>
          )}
        </section>
      </div>

      {/* SATKER + RECENT BAP */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
        <section id="satker" className="scroll-mt-[220px]">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-ink-line">
            <Building2 className="w-4 h-4 text-brass" />
            <h2 className="font-display font-bold text-[15px] tracking-tight">Distribusi Satker</h2>
            <span className="font-mono text-[10px] tracking-widest text-ink-3 ml-auto">DISTRIBUTIO</span>
          </div>
          {satkers && satkers.length > 0 ? (
            <div className="space-y-1.5 font-mono text-[11px]">
              {satkers.map((s) => {
                const max = Math.max(1, ...(satkers || []).map((x) => x.bap_count));
                const pct = (s.bap_count / max) * 100;
                return (
                  <div key={s.satker} className="grid grid-cols-[140px_1fr_auto] items-center gap-3">
                    <span className="text-ink truncate">{s.satker}</span>
                    <div className="h-3 bg-paper-2/30 border border-ink-line/40 relative">
                      <div className="h-full bg-bluestamp" style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className="text-right tabular-nums">{s.bap_count} <span className="text-ink-3 text-[9px]">/{s.pages}p</span></span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="font-mono text-xs text-ink-3 italic">Belum ada data satker.</p>
          )}
        </section>

        <section id="recent" className="scroll-mt-[220px]">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-ink-line">
            <FileText className="w-4 h-4 text-brass" />
            <h2 className="font-display font-bold text-[15px] tracking-tight">BAP Terbaru</h2>
            <span className="font-mono text-[10px] tracking-widest text-ink-3 ml-auto">RECENTIA</span>
          </div>
          {recent && recent.length > 0 ? (
            <div className="border border-ink-line">
              {recent.map((d) => (
                <Link
                  to={`/berkas/${d.id}`}
                  key={d.id}
                  className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-3 py-2 border-b border-ink-line/60 last:border-b-0 hover:bg-brass-soft/30 font-mono text-[11px]"
                >
                  <div>
                    <div className="font-display font-bold text-[12px]">{d.bap_number || '— Tanpa nomor'}</div>
                    <div className="text-ink-3 text-[10px]">{d.satker} · {d.page_count || '—'} hal</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider ${KLAS_COLOR[d.classification] || 'text-ink-2'}`}>
                    {d.classification.replace('_', ' ')}
                  </span>
                  <span className={`text-[10px] uppercase tracking-wider ${d.status === 'READY' ? 'text-olive' : 'text-bluestamp'}`}>
                    {d.status}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <p className="font-mono text-xs text-ink-3 italic">Belum ada BAP terdaftar.</p>
          )}
        </section>
      </div>

      {/* SYSTEM STATUS */}
      {kpi && (
        <div className="border-t-2 border-ink pt-4 mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 font-mono text-[11px]">
          <div>
            <Users className="w-3 h-3 inline-block mr-1 text-brass" />
            <span className="text-ink-3 uppercase tracking-wider">Pengguna aktif:</span>{' '}
            <strong className="text-ink">{kpi.total_users}</strong>
          </div>
          <div>
            <Activity className="w-3 h-3 inline-block mr-1 text-brass" />
            <span className="text-ink-3 uppercase tracking-wider">Audit events hari ini:</span>{' '}
            <strong className="text-ink">{kpi.audit_events_today}</strong>{' '}
            <span className="text-olive">· hash chain ✓</span>
          </div>
          <div>
            <FileText className="w-3 h-3 inline-block mr-1 text-brass" />
            <span className="text-ink-3 uppercase tracking-wider">Total halaman dianalisis:</span>{' '}
            <strong className="text-ink">{kpi.pages_analyzed.toLocaleString('id-ID')}</strong>
          </div>
        </div>
      )}
    </div>
  );
}

function KPICell({
  Icon,
  label,
  value,
  foot,
  last,
}: {
  Icon: any;
  label: string;
  value: number;
  foot: string;
  last?: boolean;
}) {
  return (
    <div className={`px-5 py-4 flex flex-col gap-2 ${last ? '' : 'border-r border-ink-line'}`}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-brass" />
        <div className="label-mono">{label}</div>
      </div>
      <div className="font-display font-bold text-[40px] leading-none tracking-tighter tabular-nums">
        {value.toLocaleString('id-ID')}
      </div>
      <div className="pt-2 border-t border-dashed border-ink-line font-mono text-[9px] tracking-wider text-ink-3 uppercase">
        {foot}
      </div>
    </div>
  );
}

function SparklineChart({ data }: { data: TrendPoint[] }) {
  if (!data || data.length === 0) return <p className="font-mono text-xs text-ink-3 italic">Belum ada data tren.</p>;
  const max = Math.max(1, ...data.map((d) => d.count));
  const width = 520;
  const height = 80;
  const stepX = width / Math.max(1, data.length - 1);
  const points = data.map((d, i) => `${i * stepX},${height - (d.count / max) * height}`).join(' ');
  const total = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <span className="font-display font-bold text-[28px] tabular-nums">{total}</span>
          <span className="font-mono text-[10px] tracking-widest uppercase text-ink-3 ml-2">unggah 30h</span>
        </div>
        <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3">
          Puncak {max}/hari
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-20 overflow-visible">
        <polyline points={points} fill="none" stroke="#B08A3C" strokeWidth="1.5" />
        {data.map((d, i) => (
          <circle
            key={i}
            cx={i * stepX}
            cy={height - (d.count / max) * height}
            r={d.count > 0 ? 2 : 0}
            fill="#B08A3C"
          />
        ))}
      </svg>
      <div className="flex justify-between font-mono text-[9px] text-ink-3 mt-1">
        <span>{data[0]?.day}</span>
        <span>{data[data.length - 1]?.day}</span>
      </div>
    </div>
  );
}
